import { useMemo } from "react";
import { Home, FileText, Receipt, MessageSquare, Download } from "lucide-react";
import DashboardLayout from "../../components/DashboardLayout";
import { Card, CardContent } from "../../components/ui/card";
import { DataTable, type TableColumnDef } from "../../components/data-table";
import { Button } from "../../components/ui/button";
import { useAuth } from "../../contexts/AuthContext";

type ReceiptRow = {
  date: string;
  project: string;
  amount: string;
};

export default function TaxReceipts() {
  const { user } = useAuth();

  const sidebarItems = [
    {
      label: "Dashboard",
      path: "/dashboard/donor",
      icon: <Home className="h-5 w-5" />,
    },
    {
      label: "Impact Reports",
      path: "/donor/impact-reports",
      icon: <FileText className="h-5 w-5" />,
    },
    {
      label: "Tax Receipts",
      path: "/donor/tax-receipts",
      icon: <Receipt className="h-5 w-5" />,
    },
    {
      label: "Volunteer Sign",
      path: "/donor/volunteer",
      icon: <MessageSquare className="h-5 w-5" />,
    },
  ];

  const receipts: ReceiptRow[] = [
    {
      date: "2024-01-15",
      project: "Greenwood Hostel - Library",
      amount: "₹5,00,000",
    },
    {
      date: "2024-02-20",
      project: "Riverside Hostel - Infrastructure",
      amount: "₹7,50,000",
    },
    {
      date: "2024-03-10",
      project: "Hillside Hostel - Sports Facilities",
      amount: "₹3,00,000",
    },
  ];

  const tableColumns = useMemo<TableColumnDef<ReceiptRow>[]>(
    () => [
      {
        id: "date",
        label: "Date",
        width: 120,
        renderCell: receipt => receipt.date,
      },
      {
        id: "project",
        label: "Project / Hostel",
        width: 280,
        renderCell: receipt => (
          <span className="font-medium">{receipt.project}</span>
        ),
      },
      {
        id: "amount",
        label: "Amount",
        width: 120,
        headerClassName: "text-right",
        cellClassName: "text-right font-semibold",
        renderCell: receipt => receipt.amount,
      },
      {
        id: "receipt",
        label: "Receipt",
        width: 140,
        alwaysVisible: true,
        headerClassName: "text-right",
        cellClassName: "text-right",
        renderCell: () => (
          <Button size="sm" variant="outline">
            <Download className="mr-2 h-4 w-4" />
            Download
          </Button>
        ),
      },
    ],
    []
  );

  return (
    <DashboardLayout
      sidebarItems={sidebarItems}
      currentPath="/donor/tax-receipts"
      userName={user?.email ?? "Donor"}
      userPhone=""
    >
      <div className="space-y-6">
        <div>
          <h2 className="mb-2 text-3xl font-bold text-foreground">
            Tax Receipts
          </h2>
          <p className="text-muted-foreground">
            Download your donation receipts for tax purposes
          </p>
        </div>

        <Card>
          <CardContent className="p-3 pb-4">
            <DataTable
              title={`Your Donation Receipts (${receipts.length})`}
              tableId="donor-tax-receipts"
              columns={tableColumns}
              data={receipts}
              getRowKey={receipt => `${receipt.date}-${receipt.project}`}
            />
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
